import React from "react";
import { Repeat } from "@mui/icons-material";
import { Chip, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { bookingHasSeriesId } from "./functions/booking-has-series-id.function";
import { SCHEDULE_CALENDAR_EVENT_ICON_MIN_WIDTH_PX } from "./schedule-calendar-event-icon-min-width.constant";

/**
 * Calendar chips need a tiny contrast-aware icon.
 * Booking detail panels need a labeled chip that stays visible.
 */
export type SeriesBookingIndicatorSurface = "calendar" | "detail";

type P = {
  seriesId: string | null | undefined;
  /** Inherit calendar event contrast (black/white on colored chips). */
  color?: "black" | "white";
  surface?: SeriesBookingIndicatorSurface;
};

/**
 * Marker when a booking belongs to a series.
 * Calendar: hidden below {@link SCHEDULE_CALENDAR_EVENT_ICON_MIN_WIDTH_PX}.
 * Detail: always shown as a labeled chip (not cramped into the title).
 */
export const ScheduleSeriesBookingIndicator: React.FC<P> = ({
  seriesId,
  color,
  surface = "calendar"
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const hideEventIcons = useMediaQuery(
    theme.breakpoints.down(SCHEDULE_CALENDAR_EVENT_ICON_MIN_WIDTH_PX)
  );
  const hideForNarrowCalendar = surface === "calendar" && hideEventIcons;

  if (hideForNarrowCalendar || !bookingHasSeriesId(seriesId)) {
    return null;
  }

  const tooltip = t("_schedule_seriesBookingIndicator");

  if (surface === "detail") {
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<Repeat aria-hidden />}
        label={t("_schedule_seriesBookingChip")}
        aria-label={tooltip}
        data-testid="schedule-series-booking-indicator"
        sx={{ mt: 1, maxWidth: "100%" }}
      />
    );
  }

  return (
    <Tooltip title={<Typography>{tooltip}</Typography>}>
      <Repeat
        data-testid="schedule-series-booking-indicator"
        aria-label={tooltip}
        sx={{
          ml: 0.5,
          mb: 0.125,
          fontSize: "1.25em",
          flexShrink: 0,
          display: "block",
          color: color ?? "inherit"
        }}
      />
    </Tooltip>
  );
};
